import { getUser, updateUser } from '@packages/firebase/client';
import { Alert, Button, Input } from '@packages/ui/core';
import { useSubmit } from '@remix-run/react';
import { withZod } from '@remix-validated-form/with-zod';
import {
  MultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  PhoneMultiFactorInfo,
  RecaptchaVerifier,
  browserLocalPersistence,
  getMultiFactorResolver,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import { useEffect, useRef, useState } from 'react';
import { ValidatedForm, useField, useIsSubmitting, useIsValid } from 'remix-validated-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { MfaVerificationForm } from '~/components/mfa/mfa-verification-form';
import { FirebaseManager } from '~/firebase-manager';
import { getFirebaseAuthErrorMessage } from '~/shared/utils/get-firebase-auth-error-message';
import { MIN_PASSWORD_SCORE, evaluatePassword } from '~/utils/password-strength';

export const SignInFormValidator = withZod(
  z.object({
    email: z.string().trim().toLowerCase().min(1, { message: 'Email is required.' }).email('Enter a valid email.'),
    password: z.string().min(1, { message: 'Password is required.' }),
    redirectUrl: z.string().optional(),
  }),
);

type SignInFormData = {
  email: string;
  password: string;
  redirectUrl?: string;
};

type InternalMfaChallenge = {
  resolver: MultiFactorResolver;
  verificationId: string;
  phoneNumber?: string | null;
  action: string;
  redirectUrl?: string;
  email: string;
};

export type SignInMfaChallenge = {
  phoneNumber?: string | null;
};

type UseSignInFormSubmitResult = {
  onSubmit: (data: SignInFormData, action?: string) => Promise<void>;
  error?: string;
  mfaChallenge: SignInMfaChallenge | null;
  submitMfaCode: (code: string) => Promise<void>;
  cancelMfaChallenge: () => void;
  isSubmittingMfa: boolean;
  mfaError?: string;
  isSendingMfaCode: boolean;
};

type SignInFormProps = {
  error?: string;
  forgotPasswordUrl?: string;
  onSubmit: (data: SignInFormData) => void;
  redirectUrl?: string;
  mfaChallenge?: SignInMfaChallenge | null;
  onMfaSubmit?: (code: string) => void;
  onMfaCancel?: () => void;
  isSubmittingMfa?: boolean;
  mfaError?: string;
  isSendingMfaCode?: boolean;
};

export function useSignInFormSubmit(): UseSignInFormSubmitResult {
  const submit = useSubmit();

  const [error, setError] = useState<string>();
  const [internalMfaChallenge, setInternalMfaChallenge] = useState<InternalMfaChallenge | null>(null);
  const [mfaError, setMfaError] = useState<string>();
  const [isSubmittingMfa, setIsSubmittingMfa] = useState(false);
  const [isSendingMfaCode, setIsSendingMfaCode] = useState(false);

  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);

  const clearRecaptchaVerifier = () => {
    const verifier = recaptchaVerifierRef.current;
    if (!verifier) {
      return;
    }

    try {
      verifier.clear();
    } catch (error) {
      // Ignore redundant clear attempts
    } finally {
      recaptchaVerifierRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      clearRecaptchaVerifier();
      // Also clean up the DOM container
      const container = document.getElementById('recaptcha-container-signin');
      if (container?.parentNode) {
        container.parentNode.removeChild(container);
      }
    };
  }, []);

  return {
    onSubmit: async (data: SignInFormData, action = '/sign-in') => {
      const { auth, firestore } = FirebaseManager.getApp(data.email);

      if (auth.currentUser) {
        toast.error(`You are already signed in as ${auth.currentUser.email}.`);
        return;
      }

      try {
        setError(undefined);
        setMfaError(undefined);
        setInternalMfaChallenge(null);

        await auth.setPersistence(browserLocalPersistence);

        try {
          const { user } = await signInWithEmailAndPassword(auth, data.email, data.password);

          void getUser(firestore, user.uid)
            .then(userData => {
              if (!userData || userData.hasSecurePassword != null) {
                return;
              }

              const userInputs = [userData.email, userData.firstname, userData.lastname]
                .map(value => value?.trim().toLowerCase())
                .filter((value): value is string => Boolean(value));

              const result = evaluatePassword(data.password, userInputs);
              const hasSecurePassword = result.score >= MIN_PASSWORD_SCORE;

              updateUser(firestore, user.uid, { hasSecurePassword }).catch(updateErr => {
                console.error('Failed to update hasSecurePassword during sign-in:', updateErr);
              });
            })
            .catch(fetchErr => {
              console.error('Failed to fetch user data for hasSecurePassword update during sign-in:', fetchErr);
            });

          const token = await user.getIdToken();

          const formData = new FormData();
          formData.append('token', token);
          formData.append('redirectUrl', data.redirectUrl ?? '/');

          submit(formData, { method: 'POST', action });
        } catch (error: any) {
          if (error.code === 'auth/multi-factor-auth-required') {
            setIsSendingMfaCode(true);
            try {
              const resolver = getMultiFactorResolver(auth, error);
              const hint = resolver.hints[0];

              if (hint.factorId !== PhoneMultiFactorGenerator.FACTOR_ID) {
                setError('Unsupported multi-factor method. Please contact support.');
                return;
              }

              // Aggressively clear and recreate the container element
              const oldContainer = document.getElementById('recaptcha-container-signin');
              if (oldContainer?.parentNode) {
                oldContainer.parentNode.removeChild(oldContainer);
              }

              // Create a fresh container
              const newContainer = document.createElement('div');
              newContainer.id = 'recaptcha-container-signin';
              document.body.appendChild(newContainer);

              // Clear the verifier ref
              recaptchaVerifierRef.current = null;

              const recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container-signin', {
                size: 'invisible',
              });
              recaptchaVerifierRef.current = recaptchaVerifier;

              try {
                const phoneAuthProvider = new PhoneAuthProvider(auth);
                const verificationId = await phoneAuthProvider.verifyPhoneNumber(
                  {
                    multiFactorHint: hint,
                    session: resolver.session,
                  },
                  recaptchaVerifier,
                );

                setInternalMfaChallenge({
                  resolver,
                  verificationId,
                  phoneNumber: (hint as PhoneMultiFactorInfo).phoneNumber,
                  action,
                  redirectUrl: data.redirectUrl,
                  email: data.email,
                });
                setMfaError(undefined);
              } finally {
                clearRecaptchaVerifier();
              }
            } catch (mfaError: any) {
              setError(getFirebaseAuthErrorMessage(mfaError.code) ?? 'Failed to send verification code. Please try again.');
            } finally {
              setIsSendingMfaCode(false);
            }

            return;
          }

          throw error;
        }
      } catch (err: any) {
        setError(getFirebaseAuthErrorMessage(err.code) ?? err.message ?? 'Unable to sign in.');
      }
    },
    error,
    mfaChallenge: internalMfaChallenge
      ? {
          phoneNumber: internalMfaChallenge.phoneNumber,
        }
      : null,
    submitMfaCode: async (code: string) => {
      if (!internalMfaChallenge) {
        return;
      }

      const normalizedCode = code.replace(/\s+/g, '');

      if (normalizedCode.length !== 6) {
        setMfaError('Please enter the 6-digit verification code.');
        return;
      }

      setIsSubmittingMfa(true);
      setMfaError(undefined);

      try {
        const cred = PhoneAuthProvider.credential(internalMfaChallenge.verificationId, normalizedCode);
        const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);

        const result = await internalMfaChallenge.resolver.resolveSignIn(multiFactorAssertion);
        const token = await result.user.getIdToken();

        const formData = new FormData();
        formData.append('token', token);
        formData.append('redirectUrl', internalMfaChallenge.redirectUrl ?? '/');

        submit(formData, { method: 'POST', action: internalMfaChallenge.action });
      } catch (err: any) {
        setMfaError(getFirebaseAuthErrorMessage(err.code) ?? err.message ?? 'Invalid verification code. Please try again.');
      } finally {
        setIsSubmittingMfa(false);
      }
    },
    cancelMfaChallenge: () => {
      clearRecaptchaVerifier();
      // Also clean up the DOM container
      const container = document.getElementById('recaptcha-container-signin');
      if (container?.parentNode) {
        container.parentNode.removeChild(container);
      }
      setInternalMfaChallenge(null);
      setMfaError(undefined);
      setError(undefined);
    },
    isSubmittingMfa,
    mfaError,
    isSendingMfaCode,
  };
}

export const SignInForm = ({
  error,
  forgotPasswordUrl = '/forgot-password',
  onSubmit,
  redirectUrl,
  mfaChallenge,
  onMfaSubmit,
  onMfaCancel,
  isSubmittingMfa,
  mfaError,
  isSendingMfaCode,
}: SignInFormProps) => {
  const email = useField('email', { formId: 'sign-in' });
  const password = useField('password', { formId: 'sign-in' });

  const isValid = useIsValid('sign-in');
  const isSubmitting = useIsSubmitting('sign-in');

  const [mfaCode, setMfaCode] = useState('');

  useEffect(() => {
    setMfaCode('');
  }, [mfaChallenge?.phoneNumber]);

  const hasMfaChallenge = Boolean(mfaChallenge);
  const disabled = isSubmitting || !isValid || Boolean(isSendingMfaCode);
  const isLoading = isSubmitting || Boolean(isSendingMfaCode);

  return (
    <ValidatedForm
      id="sign-in"
      validator={SignInFormValidator}
      onSubmit={(data, event) => {
        event.preventDefault();
        onSubmit(data as SignInFormData);
      }}
      method="POST"
    >
      {redirectUrl && <input type="hidden" name="redirectUrl" value={redirectUrl} />}
      <div className="space-y-6">
        <div className="space-y-4">
          {error && (
            <Alert.Root color="danger">
              <Alert.Title>Something went wrong!</Alert.Title>
              <Alert.Description>{error}</Alert.Description>
            </Alert.Root>
          )}

          {!hasMfaChallenge ? (
            <>
              <Input.Root {...email.getInputProps()} type="email" placeholder="name@example.com" disabled={isLoading}>
                <Input.Header>
                  <Input.Label>Email</Input.Label>
                </Input.Header>

                <Input.Field />

                {email.error && <Input.Error>{email.error}</Input.Error>}
              </Input.Root>

              <Input.Root {...password.getInputProps()} type="password" disabled={isLoading}>
                <Input.Header>
                  <Input.Label>Password</Input.Label>
                  <Input.CornerHint>
                    <a href={forgotPasswordUrl} tabIndex={-1}>Forgot Password?</a>
                  </Input.CornerHint>
                </Input.Header>

                <Input.Field />

                {password.error && <Input.Error>{password.error}</Input.Error>}
              </Input.Root>
            </>
          ) : (
            <div className="space-y-4 rounded-md border border-gray-200 bg-white p-4 shadow-sm">
              <div className="space-y-1 text-sm text-gray-700">
                <p>Enter the verification code sent to {mfaChallenge?.phoneNumber ?? 'your phone'} to finish signing in.</p>
                <p className="text-gray-500">Didn&apos;t get a code? Wait a few seconds and check your messages before trying again.</p>
              </div>

              <MfaVerificationForm
                value={mfaCode}
                onChange={value => setMfaCode(value)}
                onSubmit={() => {
                  if (onMfaSubmit) {
                    onMfaSubmit(mfaCode);
                  }
                }}
                onCancel={() => {
                  setMfaCode('');
                  onMfaCancel?.();
                }}
                disabled={Boolean(isSubmittingMfa)}
                error={mfaError}
                submitLabel="Verify & Sign In"
                cancelLabel="Back to Sign In"
                submitColor="primary"
              />
            </div>
          )}
        </div>

        <div id="recaptcha-container-signin"></div>

        {!hasMfaChallenge && (
          <Button name="sign-in-submit" type="submit" size="lg" disabled={disabled} isLoading={isLoading} isFullWidth>
            Sign In
          </Button>
        )}
      </div>
    </ValidatedForm>
  );
};
